/**
 * Mundalo - API error message extractor.
 *
 * FastAPI's HTTPException.detail can be either a string OR a structured
 * object (e.g. `{error: "tier_required", message: "Upgrade your plan..."}`),
 * and our `/api/city-guides/{slug}/checkout` returns the structured form
 * for 402 responses. Rendering `d.detail` directly via `alert()` or any
 * string-coercion path produces "[object Object]", which is exactly the
 * production bug we saw on the Purchase City Guide flow.
 *
 * Use `extractApiError(payload, fallback)` to get a human-readable message
 * regardless of which shape the backend returned.
 */
export function extractApiError(payload, fallback = 'Something went wrong - please try again.') {
  if (!payload) return fallback;

  // Top-level string payload (rare, but possible from network helpers)
  if (typeof payload === 'string') return payload;

  const detail = payload.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  if (detail && typeof detail === 'object') {
    // FastAPI structured-detail (our 402 paywall + tier_required shape)
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;

    // Common alternates
    if (typeof detail.error === 'string' && detail.error.trim()) {
      return detail.error.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
    }

    // Pydantic ValidationError - `detail` is an array of {msg, loc, type}
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0];
      if (first && typeof first.msg === 'string') return first.msg;
    }
  }

  // Some endpoints return {message: "..."} at the top level
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;

  return fallback;
}
